import { ExclusionInputType } from './exclusion-input-type'
import { GroupedRule } from './rule-group'
import { UrlPatternType } from './url-pattern-type'
import { ExclusionType } from './exclusion-type'
import { Exclusion, ExclusionRequest } from './api/requests/shared'
import {
  isAllAssessRulesFound,
  isAllProtectRulesFound,
  isAllRulesInferred
} from './rules-helper'
import { allRulesType } from './all-rules'
import { UserInputType } from '../attack-events-grid/shared'
import { FieldErrorItem } from '../../api/fetch/decorate-fetch'
import { QueuePatternType } from './queue-pattern-type'
import { ExclusionErrorForm } from './exclusion-form-manager'

export interface ExclusionFormData {
  exclusionName: string
  exclusionType: ExclusionType
  codes?: string
  urls?: string
  queues?: string
  inputType?: ExclusionInputType
  inputName?: string
  urlPatternType?: UrlPatternType
  queuePatternType?: QueuePatternType
}

export interface AttackEventForExclusion {
  uuid: string
  sourceIP?: string
  url?: string
  userInput?: {
    name?: string
    type: UserInputType
  }
  ruleUuid: string
}

/* Helpers */

export function normalizeExclusionForm(
  {
    exclusionName,
    exclusionType,
    urls,
    codes,
    inputType,
    inputName,
    urlPatternType,
    queuePatternType,
    queues
  }: ExclusionFormData,
  selectedRules: GroupedRule[],
  eventUuid?: string
): ExclusionRequest {
  return {
    name: exclusionName,
    type: exclusionType,
    protectRules: selectedRules
      .filter(
        (s) =>
          s.value !== allRulesType.ALL_PROTECT &&
          (s.group === 'PROTECT' || s.group === 'BOTH')
      )
      .map((s) => s.value),
    assessRules: selectedRules
      .filter(
        (s) =>
          s.value !== allRulesType.ALL_ASSESS &&
          (s.group === 'ASSESS' || s.group === 'BOTH')
      )
      .map((s) => s.value),
    allRules: isAllRulesInferred(selectedRules),
    allProtectRules: isAllProtectRulesFound(selectedRules),
    allAssessRules: isAllAssessRulesFound(selectedRules),
    urls: urls ? getLines(urls) : [],
    codes: codes ? getLines(codes) : [],
    inputType,
    inputName,
    urlPatternType,
    queuePatternType,
    eventUuid,
    queues: queues ? getLines(queues) : []
  }
}

export function getLines(input: string): string[] {
  const validLines: string[] = []

  if (input) {
    const lines = input.split('\n')
    lines.forEach((line) => {
      const trimmedLine = line.trim()
      if (trimmedLine.length > 0) {
        validLines.push(trimmedLine)
      }
    })
  }

  return validLines
}

export const initDefaultInputExclusion = (): ExclusionFormData => ({
  exclusionType: 'INPUT',
  exclusionName: '',
  codes: '',
  inputType: 'PARAMETER',
  inputName: '',
  urls: '',
  urlPatternType: 'ALL',
  queuePatternType: 'ALL',
  queues: ''
})

export const initExclusionFormFromExclusion = ({
  name,
  type,
  inputType,
  inputName,
  urlPatternType,
  urls,
  codes,
  queuePatternType,
  queues
}: Exclusion): ExclusionFormData => {
  return {
    exclusionName: name,
    exclusionType: type,
    inputType,
    inputName,
    urlPatternType,
    queuePatternType,
    urls: transformLines(urls),
    codes: transformLines(codes),
    queues: transformLines(queues)
  }
}

export function transformLines(input: string[]) {
  return input ? input.join('\n') : ''
}

export const initExclusionFromAttackEvent = (
  ruleUuid: string,
  userInput?: {
    name?: string
    type: UserInputType
  },
  sourceIP?: string,
  url?: string
): ExclusionFormData => {
  const baseForm = {
    exclusionName: ''
  }

  if (!sourceIP) {
    return { ...baseForm, exclusionType: 'CODE' }
  }

  const baseFormWithUrls = {
    ...baseForm,
    urlPatternType: 'ONLY' as UrlPatternType,
    urls: url
  }

  const defaultExclusion = {
    ...baseFormWithUrls,
    exclusionType: 'URL' as ExclusionType
  }

  if (!userInput) {
    return defaultExclusion
  }

  // see https://contrast.atlassian.net/browse/TS-15396?focusedCommentId=283068
  if (ruleUuid === 'untrusted-deserialization' && userInput.type === 'BODY') {
    return defaultExclusion
  }

  switch (userInput.type) {
    case 'PARAMETER_NAME':
    case 'PARAMETER_VALUE':
      return {
        ...baseFormWithUrls,
        exclusionType: 'INPUT',
        inputType: 'PARAMETER',
        inputName: userInput.name
      }
    case 'COOKIE_NAME':
    case 'COOKIE_VALUE':
      return {
        ...baseFormWithUrls,
        exclusionType: 'INPUT',
        inputType: 'COOKIE',
        inputName: userInput.name
      }
    case 'HEADER':
      return {
        ...baseFormWithUrls,
        exclusionType: 'INPUT',
        inputType: 'HEADER',
        inputName: userInput.name
      }
    case 'QUERYSTRING':
      return {
        ...baseFormWithUrls,
        exclusionType: 'INPUT',
        inputType: 'QUERYSTRING'
      }
    case 'BODY':
      return {
        ...baseFormWithUrls,
        exclusionType: 'INPUT',
        inputType: 'BODY'
      }
    default:
      return defaultExclusion
  }
}

export const isFormInvalid = (formIsValid: boolean, exclusionName: string) =>
  !formIsValid || exclusionName.length === 0

export const getErrorMsg = (messages: string[]) =>
  Array.isArray(messages) && messages.length > 0
    ? messages[0]
    : 'An error has occurred'

export const isBadRequest = (status: number) => status === 400

export const isDuplicateRequest = (status: number) => status === 409

export function getExclusionError(errors: FieldErrorItem[]) {
  const exclusionError: ExclusionErrorForm = {}
  errors.forEach((error) => {
    const fieldName = error.field
    const fieldMsg = error.message

    switch (fieldName) {
      case 'name':
        exclusionError.exclusionName = fieldMsg
        break
      case 'urls':
        exclusionError.urls = fieldMsg
        break
      case 'codes':
        exclusionError.codes = fieldMsg
        break
      case 'queues':
        exclusionError.queues = fieldMsg
        break
      case 'inputName':
        exclusionError.inputName = fieldMsg
        break
      default:
        break
    }
  })

  return exclusionError
}
